import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, CreditCard, Save } from "lucide-react";

export default function AdminBilling() {
  const [cfg, setCfg] = useState(null);
  const [price, setPrice] = useState("");
  const [interval, setIntervalVal] = useState("month");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/api/admin/billing/config").then(({ data }) => {
      setCfg(data);
      setPrice(((data.pro_price_cents || 0) / 100).toString());
      setIntervalVal(data.pro_interval || "month");
    }).catch(() => setCfg(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/api/admin/billing/config", {
        price_dollars: parseFloat(price),
        interval,
      });
      toast.success("Pro pricing updated. New checkouts use the new price.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't update pricing.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-billing-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold mb-1">
        <CreditCard className="w-3.5 h-3.5" /> Billing
      </div>
      <h1 className="text-2xl font-bold text-white">Pro plan pricing</h1>
      <p className="text-sm text-slate-400 mt-1">Change the Pro price and billing frequency.</p>

      {cfg === null ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-16 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : cfg === false ? (
        <div className="text-center text-slate-500 py-16 text-sm">Couldn’t load billing config.</div>
      ) : (
        <form onSubmit={save} className="mt-6 max-w-md rounded-xl border border-slate-800 bg-[#0e1726] p-6 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-300">Pro price ({(cfg.currency || "aud").toUpperCase()})</Label>
            <Input type="number" step="0.01" min="0.5" value={price} onChange={(e) => setPrice(e.target.value)}
              data-testid="pro-price-input" className="bg-[#0b1220] border-slate-700 text-white" required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-300">Billing frequency</Label>
            <select value={interval} onChange={(e) => setIntervalVal(e.target.value)}
              data-testid="pro-interval-select"
              className="w-full bg-[#0b1220] border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 focus:border-[#5ec0ff] outline-none">
              <option value="month" className="bg-[#0b1220]">Monthly</option>
              <option value="year" className="bg-[#0b1220]">Yearly</option>
            </select>
          </div>
          <Button type="submit" disabled={busy} data-testid="save-billing-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save pricing
          </Button>
          {!cfg.stripe_price_id && cfg.pro_stripe_price_id === null && (
            <p className="text-[11px] text-amber-400">Stripe price will be created when you save.</p>
          )}
        </form>
      )}
    </div>
  );
}
